import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Database, Plus } from 'lucide-react'
import { SearchInput } from '../components/SearchInput'
import { getDatasets, createDataset } from '../api/storage'
import { Modal } from '../components/Modal'
import { EmptyState } from '../components/EmptyState'
import { useDateFmt } from '../hooks/useDateFmt'
import { Field } from '../components/Field'
import { Button } from '../components/ui/button'
import type { Dataset } from '../types'

export function Datasets() {
  const fmt = useDateFmt()
  const qc = useQueryClient()
  const navigate = useNavigate()

  const { data: datasets = [], isLoading } = useQuery({
    queryKey: ['datasets'],
    queryFn: () => getDatasets().then((r) => r.data),
  })

  const [showCreate, setShowCreate] = useState(false)
  const [createForm, setCreateForm] = useState({ title: '', description: '', archivePeriodDays: '30', purgePeriodDays: '90' })
  const setCF = (k: keyof typeof createForm) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setCreateForm((f) => ({ ...f, [k]: e.target.value }))

  const create = useMutation({
    mutationFn: () => createDataset({
      title: createForm.title,
      description: createForm.description,
      archivePeriodDays: parseInt(createForm.archivePeriodDays),
      purgePeriodDays: parseInt(createForm.purgePeriodDays),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['datasets'] })
      setShowCreate(false)
      setCreateForm({ title: '', description: '', archivePeriodDays: '30', purgePeriodDays: '90' })
    },
  })

  const [search, setSearch] = useState('')
  const filtered = datasets.filter((ds: Dataset) =>
    [ds.title, ds.description ?? ''].some((v) => v.toLowerCase().includes(search.toLowerCase()))
  )

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-ink">Datasets</h1>
          <p className="text-sm text-ink-dim mt-0.5">Manage document collections and their retention policies</p>
        </div>
        <div className="flex items-center gap-2">
          <SearchInput value={search} onChange={setSearch} placeholder="Search datasets" />
          <Button icon={Plus} onClick={() => setShowCreate(true)}>New dataset</Button>
        </div>
      </div>

      <div className="table-wrap">
        {isLoading ? (
          <div className="p-8 text-center text-sm text-ink-dim">Loading</div>
        ) : datasets.length === 0 ? (
          <EmptyState icon={Database} title="No datasets" description="Create a dataset to start storing documents" />
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Archive</th>
                <th>Purge</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((ds: Dataset) => (
                <tr
                  key={ds.id}
                  onClick={() => navigate(`/datasets/${ds.id}`)}
                  className="cursor-pointer"
                >
                  <td>
                    <p className="font-medium text-ink">{ds.title}</p>
                    {ds.description && <p className="text-xs text-ink-dim mt-0.5">{ds.description}</p>}
                  </td>
                  <td className="text-ink-dim">{ds.archivePeriodDays}d</td>
                  <td className="text-ink-dim">{ds.purgePeriodDays}d</td>
                  <td className="text-ink-dim">{fmt(ds.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showCreate && (
        <Modal title="New dataset" onClose={() => setShowCreate(false)} size="sm">
          <div className="space-y-4">
            <Field label="Title *">
              <input value={createForm.title} onChange={setCF('title')} placeholder="e.g. invoices-2024"
                className="field" autoFocus />
            </Field>
            <Field label="Description">
              <input value={createForm.description} onChange={setCF('description')} placeholder="Optional"
                className="field" />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Archive after (days) *">
                <input type="number" value={createForm.archivePeriodDays} onChange={setCF('archivePeriodDays')} min="1"
                  className="field" />
              </Field>
              <Field label="Purge after (days) *">
                <input type="number" value={createForm.purgePeriodDays} onChange={setCF('purgePeriodDays')} min="2"
                  className="field" />
              </Field>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="secondary" onClick={() => setShowCreate(false)}>Cancel</Button>
              <Button onClick={() => create.mutate()} disabled={!createForm.title} loading={create.isPending}>
                Create
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  )
}
